import { ArrowRightFromSquare, Hand, DisplayPulse } from "@gravity-ui/icons";
import {
  PageLayout,
  AsideFallback,
  PageLayoutAside,
  FooterItem,
  ActionBar,
  MenuItem,
} from "@gravity-ui/navigation";
import { Button, Icon, User } from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { SVGIconData } from "node_modules/@gravity-ui/uikit/build/esm/components/Icon/types";
import { Suspense, PropsWithChildren, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appConfig } from "src/config";
import { POIZON_API_SUPPORT_LINK } from "src/constants/links";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { getMeRequest } from "src/store/fetchers";
import { logoutRequest } from "src/store/fetchers/logoutRequest";
import { isAdmin } from "src/util";
import { ShowConfirmDialog } from "src/widgets/Alert/Alert";

import { LanguagesBlock } from "../LanguagesBlock";
import { ThemeBlock } from "../ThemeBlock";
import { ServiceStatusNavigationItem } from "./components/ServiceStatusNavigationItem/ServiceStatusNavigationItem.tsx";
import { menuItems } from "./MenuLayout.constants";
import css from "./MenuLayout.module.scss";
import { isAsideCollapse } from "./store";

export const MenuLayout = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();

  const [compact, setCompact] = useAtom(isAsideCollapse);
  const [isLoading] = useAtom(logoutRequest.isLoadingAtom);
  const [userData] = useAtom(getMeRequest.dataAtom);

  const { pathname: currentPath } = useLocation();

  const logout = useAction(logoutRequest);

  const handleLogout = async () => {
    const result = await ShowConfirmDialog(
      "Вы действительно хотите выйти из аккаунта?",
      "Подтвердите выход",
    );
    if (result) logout();
  };

  const filteredMenuItems: MenuItem[] = useMemo(() => {
    return menuItems.map((value) => {
      if (value.type === "divider") {
        return value;
      }

      if (value.type === "action") {
        return {
          ...value,
          current: value.current(currentPath),
          onItemClick: () => window.open(value.path),
        };
      }

      return {
        ...value,
        current: value.current(currentPath),
        onItemClick: () => navigate(value.path),
      };
    });
  }, [currentPath, navigate]);

  return (
    <PageLayout compact={compact}>
      <Suspense fallback={<AsideFallback />}>
        <PageLayoutAside
          headerDecoration={true}
          logo={{
            icon: appConfig.logo as SVGIconData,
            iconSize: 40,
            text: "",
            href: RoutesPaths.LANDING,
          }}
          onChangeCompact={setCompact}
          menuItems={filteredMenuItems}
          renderFooter={({ compact }) => {
            return (
              <>
                <ServiceStatusNavigationItem compact={compact} />
                {isAdmin(userData) && (
                  <FooterItem
                    compact={compact}
                    item={{
                      id: "admin-panel",
                      title: "Админ панель",
                      icon: DisplayPulse,
                      current: currentPath.startsWith("/admin"),
                      onItemClick: () => {
                        navigate(RoutesPaths.ADMIN.BASE_PATH);
                      },
                    }}
                  />
                )}
                <FooterItem
                  compact={compact}
                  item={{
                    id: "logout",
                    title: "Выйти",
                    icon: ArrowRightFromSquare,
                    onItemClick: () => {
                      if (!isLoading) handleLogout();
                    },
                  }}
                />
              </>
            );
          }}
        />
      </Suspense>
      <PageLayout.Content>
        <ActionBar aria-label="Actions bar">
          <ActionBar.Section>
            <ActionBar.Group pull="left">
              <ActionBar.Item>
                <Button
                  view="flat-info"
                  href={POIZON_API_SUPPORT_LINK}
                  target="_blank"
                >
                  <Icon data={Hand} />
                  Нужна помощь?
                </Button>
              </ActionBar.Item>
            </ActionBar.Group>
            <ActionBar.Group pull="right">
              <ActionBar.Item>
                <LanguagesBlock size="m" />
              </ActionBar.Item>
              <ActionBar.Item>
                <ThemeBlock size="m" />
              </ActionBar.Item>
              <ActionBar.Item>
                <div onClick={() => navigate(RoutesPaths.LK.PROFILE.BASE_PATH)}>
                  <User
                    avatar={{
                      text: userData?.name ?? "",
                      theme: "brand",
                    }}
                    name={userData?.name}
                    description={userData?.email}
                    size="xs"
                    className={css.userCustomStyle}
                  />
                </div>
              </ActionBar.Item>
            </ActionBar.Group>
          </ActionBar.Section>
        </ActionBar>
        <div className={css.divPadding}>{children}</div>
      </PageLayout.Content>
    </PageLayout>
  );
};
